import { useMemo, useRef } from 'react';

import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { useScrollHorizontal } from '@repo/hooks';
import { getServiceName } from '@repo/utils';
import ReactECharts from 'echarts-for-react';
import EChartsReact from 'echarts-for-react';

import { useCalculateChartWidth } from './hooks/useCalculateChartWidth';

import type { DefaultLabelFormatterCallbackParams } from 'echarts';

export type Metrics = {
  name: string;
  value: number;
  valueGlobal?: number;
};

export interface EBarChartProps {
  metrics: Metrics[];
}

const EBarChart = ({ metrics }: EBarChartProps) => {
  const theme = useTheme();
  const chartRef = useRef<EChartsReact>(null);
  // 가로 스크롤을 위한 훅 사용
  const {
    containerRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
  } = useScrollHorizontal();

  // 항목당 너비 설정 (각 항목당 43px 너비)
  const barWidth = 8;
  const { chartWidth } = useCalculateChartWidth(containerRef, metrics, { minWidth: 400 });

  const hasGlobalData = useMemo(() => metrics.some((item) => item.valueGlobal != null), [metrics]);

  // 국내 (Domestic) 바 색상 - stacked 차트용 (primary)
  const getDomesticBarColor = (totalValue: number, maxValue: number) => {
    const serviceName = getServiceName();
    const isMax = totalValue === maxValue;
    // 팅글: 브랜드 primary(핑크) — 최대값 진하게 / 그 외 40% (웹과 동일 brand 색상 사용)
    if (serviceName === 'tingle') {
      return isMax ? theme.color.brand.primary : 'rgba(249, 83, 136, 0.4)';
    }
    if (isMax) return theme.color.static.primary;
    return theme.palette.aquamarine[10];
  };

  // 해외 (Global) 바 색상 - stacked 차트용 (secondary)
  const getGlobalBarColor = (totalValue: number, maxValue: number) => {
    const serviceName = getServiceName();
    const isMax = totalValue === maxValue;
    // 팅글: 브랜드 secondary(라임) — 최대값 진하게 / 그 외 40%
    if (serviceName === 'tingle') {
      return isMax ? theme.color.brand.secondary : 'rgba(255, 248, 32, 0.4)';
    }
    if (isMax) return theme.color.static.secondary;
    return theme.palette.purple[20];
  };

  // 최대값 찾기 (stacked인 경우 합산)
  const maxValue = Math.max(
    ...metrics.map((item) => (hasGlobalData ? item.value + (item.valueGlobal ?? 0) : item.value))
  );

  // 최대값을 가진 항목의 인덱스 찾기 (markPoint 위치 지정용)
  const maxIdx = metrics.findIndex((item) =>
    hasGlobalData ? item.value + (item.valueGlobal ?? 0) === maxValue : item.value === maxValue
  );

  // 텍스트 길이에 맞게 동적으로 너비를 계산 (최소 80px 보장)
  const value = maxValue.toLocaleString();
  const textWidth = value.length * 7 + 24;
  const markWidth = Math.max(textWidth, 80);
  const markHeight = 29; // 원래 높이 36px * 0.8

  const prevMarkUrlRef = useRef<string | null>(null);
  const highestValueMarkUrl = useMemo(() => {
    if (prevMarkUrlRef.current) {
      URL.revokeObjectURL(prevMarkUrlRef.current);
      prevMarkUrlRef.current = null;
    }
    if (maxValue === 0) return null;
    const url = createBlobUrlFromHighestValueMarkSvg(value, markWidth, markHeight);
    prevMarkUrlRef.current = url;
    return url;
  }, [value, metrics]);

  // ECharts 옵션 설정
  const getOption = () => ({
    tooltip: {
      trigger: hasGlobalData ? 'axis' : 'item',
      transitionDuration: 0,
      // 기본 스타일 제거
      backgroundColor: 'transparent',
      borderWidth: 0,
      borderColor: 'transparent',
      shadowColor: 'transparent',
      textStyle: {
        color: '#FFFFFF', // 텍스트 색상 설정
        fontFamily: 'Pretendard, sans-serif',
        fontSize: 11,
        fontWeight: 500,
      },
      ...(hasGlobalData
        ? {
            // stacked bar: axis trigger → params가 배열로 전달
            formatter: (params: DefaultLabelFormatterCallbackParams | DefaultLabelFormatterCallbackParams[]) => {
              if (!Array.isArray(params) || params.length === 0) return '';
              const idx = params[0].dataIndex;
              const total = metrics[idx].value + (metrics[idx].valueGlobal ?? 0);
              if (total === maxValue) return '';
              return createCustomTooltip(total.toLocaleString());
            },
            // axis trigger 시 chartRef로 bar 상단 pixel 좌표 계산
            position: (
              _point: [number, number],
              params: DefaultLabelFormatterCallbackParams[],
              _dom: HTMLDivElement | null,
              _rect: { x: number; y: number; width: number; height: number } | null,
              size: { contentSize: [number, number]; viewSize: [number, number] }
            ) => {
              if (!Array.isArray(params) || params.length === 0) return [0, 0];
              const idx = params[0].dataIndex;
              const total = metrics[idx].value + (metrics[idx].valueGlobal ?? 0);
              if (total === maxValue) return [0, 0];
              const chart = chartRef.current?.getEchartsInstance();
              if (!chart) return [0, 0];
              const pixel = chart.convertToPixel({ seriesIndex: 0 }, [idx, total]);
              const x = pixel[0] - size.contentSize[0] / 2;
              const y = pixel[1] - size.contentSize[1] - 3;
              return [x, y];
            },
            axisPointer: {
              type: 'none', // axis hover 시 세로선 숨김
            },
          }
        : {
            // single bar: item trigger
            formatter: (params: DefaultLabelFormatterCallbackParams) => {
              if (params.componentType === 'markPoint') return '';
              const paramValue = typeof params.value === 'number' ? params.value : 0;
              if (paramValue === maxValue) return '';
              return createCustomTooltip(paramValue.toLocaleString());
            },
            position: (
              _point: number[],
              _params: DefaultLabelFormatterCallbackParams | DefaultLabelFormatterCallbackParams[],
              _dom: HTMLDivElement | null,
              rect: { x: number; y: number; width: number; height: number } | null,
              size: { contentSize: [number, number]; viewSize: [number, number] }
            ) => {
              if (!rect) return [0, 0];
              const x = rect.x + rect.width / 2 - size.contentSize[0] / 2;
              const y = rect.y - size.contentSize[1] - 3;
              return [x, y];
            },
          }),
    },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      data: metrics.map((item) => item.name),
      axisLine: {
        lineStyle: {
          color: metrics.length > 0 ? '#C4C6C9' : 'transparent',
        },
      },
      axisLabel: {
        color: '#8A8A8A', // Y축 라벨 색상
        fontFamily: 'Pretendard', // 폰트 패밀리
        fontSize: 11, // 폰트 크기
        fontStyle: 'normal', // 폰트 스타일
        fontWeight: 600, // 폰트 굵기
        lineHeight: 16, // 줄 높이
      },
      axisTick: {
        show: false, // X축 눈금 제거
      },
      splitLine: {
        show: false, // X축에 그리드 라인 제거
      },
    },
    yAxis: {
      type: 'value',
      axisLine: {
        lineStyle: {
          color: metrics.length > 0 ? '#C4C6C9' : 'transparent', // X축 테두리 색상
        },
      },
      axisLabel: {
        color: '#8A8A8A', // X축 라벨 색상
        fontFamily: 'Pretendard', // 폰트 패밀리
        fontSize: 11, // 폰트 크기
        fontStyle: 'normal', // 폰트 스타일
        fontWeight: 600, // 폰트 굵기
        lineHeight: 16, // 줄 높이
      },
    },
    series: hasGlobalData
      ? [
          // 국내 (Domestic) - 아래쪽 (첫 번째 series = stack 하단)
          {
            type: 'bar',
            stack: 'total',
            data: metrics.map((item) => {
              const total = item.value + (item.valueGlobal ?? 0);
              return {
                value: item.value,
                itemStyle: {
                  color: getDomesticBarColor(total, maxValue),
                  borderRadius: (item.valueGlobal ?? 0) > 0 ? [0, 0, 0, 0] : [3, 3, 0, 0],
                },
              };
            }),
            barCategoryGap: '0%',
            barWidth,
            markPoint: {
              symbol: `image://${highestValueMarkUrl}`,
              symbolSize: [markWidth, 36],
              data: [
                {
                  coord: [maxIdx, maxValue],
                  name: 'Highest Value',
                  symbolOffset: [0, -20],
                },
              ],
              label: {
                show: false,
              },
            },
          },
          // 해외 (Global) - 위쪽 (두 번째 series = stack 상단)
          {
            type: 'bar',
            stack: 'total',
            data: metrics.map((item) => {
              const total = item.value + (item.valueGlobal ?? 0);
              return {
                value: item.valueGlobal ?? 0,
                itemStyle: {
                  color: getGlobalBarColor(total, maxValue),
                  borderRadius: [3, 3, 0, 0],
                },
              };
            }),
            barCategoryGap: '0%',
            barWidth,
          },
        ]
      : [
          {
            type: 'bar',
            data: metrics.map((item) => ({
              value: item.value,
              itemStyle: {
                color: getBarColor(item.value, maxValue),
                borderRadius: item.value > 0 ? [3, 3, 0, 0] : [0, 0, 3, 3],
              },
            })),
            barCategoryGap: '0%',
            barWidth,
            markPoint: {
              symbol: `image://${highestValueMarkUrl}`,
              symbolSize: [markWidth, 36],
              data: [
                {
                  type: 'max',
                  name: 'Highest Value',
                  symbolOffset: [0, -20],
                },
              ],
              label: {
                show: false,
              },
            },
          },
        ],
    grid: {
      left: '3%',
      right: '3%',
      bottom: '3%',
      containLabel: true,
    },
  });

  // useEffect(() => {
  //   // 최대값이 화면에 중앙에 오도록 하는 코드
  //   if (!containerRef.current || metrics.length === 0) return;

  //   // 최대값의 인덱스 찾기
  //   const maxValueIndex = metrics.findIndex((item) => item.value === maxValue);

  //   // 차트의 전체 너비
  //   const totalWidth = containerRef.current.scrollWidth;
  //   // 보이는 영역의 너비
  //   const viewportWidth = containerRef.current.clientWidth;

  //   // 각 막대의 위치 계산 (왼쪽 여백 + 막대 인덱스 * 막대 간격)
  //   const barPosition = (totalWidth / metrics.length) * maxValueIndex;

  //   // 최대값이 오른쪽 절반에 있는 경우
  //   if (maxValueIndex > metrics.length / 2) {
  //     // 최대값 막대가 보이도록 오른쪽으로 스크롤
  //     containerRef.current.scrollLeft = Math.min(totalWidth - viewportWidth, barPosition - viewportWidth / 2);
  //   } else {
  //     // 최대값이 왼쪽 절반에 있는 경우 왼쪽으로 스크롤
  //     containerRef.current.scrollLeft = Math.max(0, barPosition - viewportWidth / 2);
  //   }
  // }, [metrics, maxValue, containerRef]);

  return (
    <SChartContainer
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div style={{ width: `${chartWidth}px` }}>
        <ReactECharts ref={chartRef} option={getOption()} style={{ height: '304px', width: `${chartWidth}px` }} />
      </div>
    </SChartContainer>
  );
};

export default EBarChart;

// 스타일링된 컨테이너로 가로 스크롤 적용
const SChartContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  overflow-y: hidden;
  cursor: grab;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

// 최대값에 파란 말풍선을 띄우기 위함임
const createBlobUrlFromHighestValueMarkSvg = (value: string, width: number, height: number): string => {
  const tailSize = 5; // 꼬리 크기
  const serviceName = getServiceName();
  // 팅글: 최대값 말풍선 = 핑크 배경 + 검정 아이콘/텍스트 (버블챗은 기존 파랑+흰색)
  const backgroundColor = serviceName === 'bubblechat' ? '#00C3DD' : '#F95388';
  const iconTextColor = serviceName === 'bubblechat' ? '#FFFFFF' : '#000000';

  // SVG 문자열 생성
  const svgString = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height + tailSize}">
      <path
        d="
          M14,0 
          H${width - 14} 
          A14,14 0 0 1 ${width},14 
          V${height - 14} 
          A14,14 0 0 1 ${width - 14},${height} 
          H${width / 2 + tailSize} 
          L${width / 2},${height + tailSize} 
          L${width / 2 - tailSize},${height} 
          H14 
          A14,14 0 0 1 0,${height - 14} 
          V14 
          A14,14 0 0 1 14,0
        "
        fill="${backgroundColor}"
      />
      <g transform="translate(${width / 2}, ${height / 2})" text-anchor="middle">
        <svg width="13" height="13" x="-32" y="-6.5" viewBox="0 0 20 20" fill="none">
          <path
            d="M2 17.1579C2 16.6928 2.37702 16.3158 2.84211 16.3158H17.1579C17.623 16.3158 18 16.6928 18 17.1579C18 17.623 17.623 18 17.1579 18H2.84211C2.37702 18 2 17.623 2 17.1579ZM2 6.34064C2 5.55309 2.86812 5.07461 3.53399 5.49515L6 7.05263L9.21596 2.99037C9.61633 2.48464 10.3837 2.48464 10.7841 2.99037L14 7.05263L16.466 5.49515C17.1319 5.07461 18 5.55309 18 6.34064V13.6316C18 14.1839 17.5523 14.6316 17 14.6316H3C2.44772 14.6316 2 14.1839 2 13.6316V6.34064Z"
            fill="${iconTextColor}"
          />
        </svg>
        <text x="8" y="4" font-size="11" font-weight="700" fill="${iconTextColor}" font-family="Arial, sans-serif">
          ${value}
        </text>
      </g>
    </svg>
  `;

  // Blob 생성
  const blob = new Blob([svgString], { type: 'image/svg+xml' });

  // Blob URL 생성
  return URL.createObjectURL(blob);
};

const createCustomTooltip = (amountFormat: string): string => {
  return `
    <div style="
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      height: 29px;
    ">
      <div style="
        background: rgba(0, 0, 0, 0.8);
        padding: 0px 6px;
        border-radius: 4px;
        height: 25px;
        display: flex;
        align-items: center;
      ">
        <span style="
          color: white;
          font-family: Pretendard;
          font-size: 11px;
          font-style: normal;
          font-weight: 500;
          line-height: 17px;
        ">
          ${amountFormat}
        </span>
      </div>      
      <svg xmlns="http://www.w3.org/2000/svg" width="7" height="4" viewBox="0 0 7 4" fill="none">
        <g>
          <path d="M3.5 4L0 0H7L3.5 4Z" fill="#333333" />
        </g>
        <defs>
          <filter
            id="filter0_d_18454_2425"
            x="-3"
            y="-2"
            width="13"
            height="10"
            filterUnits="userSpaceOnUse"
            colorInterpolationFilters="sRGB"
          >
            <feFlood floodOpacity="0" result="BackgroundImageFix" />
            <feColorMatrix
              in="SourceAlpha"
              type="matrix"
              values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
              result="hardAlpha"
            />
            <feOffset dy="1" />
            <feGaussianBlur stdDeviation="1" />
            <feComposite in2="hardAlpha" operator="out" />
            <feColorMatrix type="matrix" values="0 0 0 0 0.121569 0 0 0 0 0.184314 0 0 0 0 0.27451 0 0 0 0.12 0" />
            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_18454_2425" />
            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_18454_2425" result="shape" />
          </filter>
        </defs>
      </svg>
    </div>
  `;
};

const getBarColor = (currentValue: number, maxValue: number) => {
  const serviceName = getServiceName();

  if (currentValue < 0) {
    return '#FFA7A7';
  }
  // 팅글: 브랜드 primary(핑크) — 최대값 진하게 / 그 외 40% (스택 바 및 범례와 통일)
  if (currentValue === maxValue) {
    return serviceName === 'bubblechat' ? '#00C3DD' : '#F95388';
  } else {
    return serviceName === 'bubblechat' ? '#B0EAF4' : 'rgba(249, 83, 136, 0.4)';
  }
};
